/**
 * Circuit-breaker UI consumer (web · 2026-04-19).
 *
 * Subscribes to `http.circuit_open` / `http.circuit_closed` /
 * `http.circuit_half_open` events on the Layer-4 `EventBus` and
 * exposes a tiny external store that React components consume via
 * `useCircuitBreakerStatus()`. When at least one host has an open
 * circuit, the hook returns `{ degraded: true, hosts: Set(...) }`
 * — surfaces (banner / toast / status icon) render off that.
 *
 * Mirrors the duplicate-detected consumer pattern landed earlier
 * this week: bus → consumer → external store → UI. Closes the next
 * "infrastructure shipped, no consumer" gap from the senior-eval —
 * Layer-1 `HTTPClient` has been emitting `circuit_open` events
 * since Phase B, but no UI surface has subscribed.
 *
 * Wiring:
 *   - Server boot: `instrumentation.ts` already runs
 *     `bootstrapTelemetry({ ... })` at startup.
 *   - Client boot: import `attachCircuitBreakerConsumer()` from a
 *     root client provider (e.g. a `<TelemetryConsumersProvider>`
 *     mounted in the root layout). Returns a disposer.
 *   - Tests: subscribe to a fresh `EventBus` directly via
 *     `attachCircuitBreakerConsumer(bus)`.
 *
 * Auto-dismiss semantics:
 *   - `http.circuit_open { host }`  → add host to open set.
 *   - `http.circuit_closed { host }`/ `http.circuit_half_open { host }`
 *     → remove host from open set.
 *   - When the set transitions to empty, the banner hides.
 */

import { useSyncExternalStore } from 'react'
import {
    getEventBus,
    type EventBus,
    type TelemetryEvent,
} from '@/lib/telemetry/eventBus'

// ─── Store ──────────────────────────────────────────────────────────

interface CircuitStatusState {
    /** Hosts whose circuits are currently OPEN (degraded). */
    openHosts: ReadonlySet<string>
}

type Listener = (state: CircuitStatusState) => void

class CircuitStatusStore {
    private state: CircuitStatusState = { openHosts: new Set() }
    private listeners = new Set<Listener>()
    private cachedSnapshot: CircuitStatusState = this.state

    getState(): CircuitStatusState {
        return this.cachedSnapshot
    }

    subscribe(listener: Listener): () => void {
        this.listeners.add(listener)
        return () => this.listeners.delete(listener)
    }

    open(host: string): void {
        const next = new Set(this.state.openHosts)
        if (next.has(host)) return
        next.add(host)
        this.commit({ openHosts: next })
    }

    close(host: string): void {
        if (!this.state.openHosts.has(host)) return
        const next = new Set(this.state.openHosts)
        next.delete(host)
        this.commit({ openHosts: next })
    }

    /** Test-only — drains every host. */
    reset(): void {
        if (this.state.openHosts.size === 0) return
        this.commit({ openHosts: new Set() })
    }

    private commit(state: CircuitStatusState): void {
        this.state = state
        // Fresh snapshot — useSyncExternalStore relies on identity
        // equality to skip re-renders.
        this.cachedSnapshot = state
        for (const listener of this.listeners) listener(state)
    }
}

let sharedStore: CircuitStatusStore | null = null

function getStore(): CircuitStatusStore {
    if (!sharedStore) sharedStore = new CircuitStatusStore()
    return sharedStore
}

// ─── Bus consumer ───────────────────────────────────────────────────

const HOST_KIND_OPEN: TelemetryEvent['kind'] = 'http.circuit_open'
const HOST_KIND_CLOSED: TelemetryEvent['kind'] = 'http.circuit_closed'
const HOST_KIND_HALF_OPEN: TelemetryEvent['kind'] = 'http.circuit_half_open'

/**
 * Attaches the consumer to a bus. Returns the disposer that
 * unsubscribes. Call once at client bootstrap; idempotent across
 * multiple mounts because subscriptions go to the shared store.
 */
export function attachCircuitBreakerConsumer(
    bus: EventBus = getEventBus(),
    store: CircuitStatusStore = getStore(),
): () => void {
    return bus.subscribe((event) => {
        if (event.kind === HOST_KIND_OPEN) {
            const host = (event as Extract<TelemetryEvent, { kind: 'http.circuit_open' }>).host
            if (host) store.open(host)
        } else if (event.kind === HOST_KIND_CLOSED || event.kind === HOST_KIND_HALF_OPEN) {
            const host = (event as Extract<TelemetryEvent, { kind: 'http.circuit_closed' | 'http.circuit_half_open' }>).host
            if (host) store.close(host)
        }
    })
}

// ─── React hook ─────────────────────────────────────────────────────

/**
 * React hook that returns the live circuit-breaker status. Call from
 * any client component that wants to render a "service degraded"
 * banner. The returned `degraded` boolean is stable across
 * non-meaningful re-renders.
 */
export function useCircuitBreakerStatus(): {
    degraded: boolean
    hosts: ReadonlySet<string>
} {
    const store = getStore()
    const state = useSyncExternalStore(
        (listener) => store.subscribe(listener),
        () => store.getState(),
        () => store.getState(),
    )
    return {
        degraded: state.openHosts.size > 0,
        hosts: state.openHosts,
    }
}

/**
 * User-visible copy for the degraded-banner. Centralised here so
 * UX iteration is one file change.
 */
export function circuitBannerCopy(hosts: ReadonlySet<string>): {
    title: string
    detail: string
} {
    const list = Array.from(hosts)
    if (list.length === 0) {
        return { title: '', detail: '' }
    }
    if (list.length === 1) {
        return {
            title: 'Connection issues — retrying',
            detail: `Having trouble reaching ${list[0]}. We'll keep trying.`,
        }
    }
    return {
        title: 'Connection issues — retrying',
        detail: `Having trouble reaching ${list.length} services. We'll keep trying.`,
    }
}

// ─── Test seam ──────────────────────────────────────────────────────

export function resetCircuitBreakerStoreForTesting(): void {
    if (sharedStore) sharedStore.reset()
    sharedStore = null
}
